import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot, type Firestore } from 'firebase/firestore';
import {
  assertGroupMember,
  type GroupMember,
  type Group,
} from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import { useAuth } from '../../../app/providers/AuthProvider';
import { requestMembership, leaveMembership } from '../api/groupRepository';

export type MembershipStatus = 'not_member' | 'pending' | 'active' | 'loading';

export type MembershipState = {
  readonly member: GroupMember | null;
  readonly status: MembershipStatus;
  readonly loading: boolean;
  readonly requesting: boolean;
  readonly leaving: boolean;
  readonly error: Error | null;
  readonly canJoin: boolean;
  readonly canLeave: boolean;
  readonly joinGroup: () => Promise<void>;
  readonly leaveGroup: () => Promise<void>;
  readonly getStatusMessage: () => string;
};

const getMemberDocument = (firestore: Firestore, groupId: string, uid: string) =>
  doc(firestore, `groups/${groupId}/members/${uid}`);

export const useMembership = (
  groupId: string | null,
  uid: string | null,
  group?: Group | null,
): MembershipState => {
  const { firestore } = useFirebaseServices();
  const { profile } = useAuth();
  const [member, setMember] = useState<GroupMember | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(groupId && uid));
  const [requesting, setRequesting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Determine membership status
  const status: MembershipStatus = loading
    ? 'loading'
    : member?.status === 'active'
      ? 'active'
      : member?.status === 'pending'
        ? 'pending'
        : 'not_member';

  // Check permissions
  const canJoin =
    !requesting && !leaving && status === 'not_member' && Boolean(groupId && uid);
  const canLeave =
    !requesting && !leaving && (status === 'active' || status === 'pending');

  // Status message based on membership state and group settings
  const getStatusMessage = useCallback((): string => {
    switch (status) {
      case 'loading':
        return 'Loading membership status...';
      case 'active':
        return 'You are a member of this group';
      case 'pending':
        if (group?.autoAcceptMembers) {
          return 'Your membership is being processed...';
        }
        return 'Your membership request is pending organizer approval';
      case 'not_member':
      default:
        if (group?.autoAcceptMembers) {
          return 'Join this group instantly';
        }
        return 'Request to join this group';
    }
  }, [status, group?.autoAcceptMembers]);

  // Join group with differentiated flow
  const joinGroup = useCallback(async (): Promise<void> => {
    if (!canJoin || !groupId || !uid || !group || !profile) {
      return;
    }

    setRequesting(true);
    setError(null);

    try {
      const membershipPayload = await requestMembership(firestore, group, {
        uid,
        role: 'member', // New members always join as members
        displayName: profile.displayName,
        email: profile.email,
        photoURL: profile.photoURL || null, // Convert undefined to null
      });

      // Optimistic update - set member immediately
      setMember(membershipPayload);

      // Member will be immediately active if auto-accept is enabled
      // Otherwise member will be pending approval
      // Feedback will be handled by the calling component
    } catch (joinError) {
      setError(
        joinError instanceof Error ? joinError : new Error('Failed to join group'),
      );

      // Revert optimistic update
      setMember(null);
    } finally {
      setRequesting(false);
    }
  }, [canJoin, groupId, uid, group, profile, firestore]);

  // Leave group
  const leaveGroup = useCallback(async (): Promise<void> => {
    if (!canLeave || !groupId || !uid) {
      return;
    }

    setLeaving(true);
    setError(null);

    try {
      // Optimistic update
      setMember(null);

      await leaveMembership(firestore, groupId, uid);

      // Feedback will be handled by the calling component
    } catch (leaveError) {
      setError(
        leaveError instanceof Error ? leaveError : new Error('Failed to leave group'),
      );

      // Note: Don't revert optimistic update here as the real-time
      // subscription will restore the correct state if the operation failed
    } finally {
      setLeaving(false);
    }
  }, [canLeave, groupId, uid, firestore]);

  // Subscribe to membership changes
  useEffect(() => {
    if (!groupId || !uid) {
      setMember(null);
      setLoading(false);
      setError(null);
      return () => undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      getMemberDocument(firestore, groupId, uid),
      snapshot => {
        if (!snapshot.exists()) {
          setMember(null);
        } else {
          const data = snapshot.data();
          setMember(assertGroupMember({ uid, ...data }));
        }
        setLoading(false);
      },
      snapshotError => {
        setError(snapshotError);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firestore, groupId, uid]);

  return {
    member,
    status,
    loading,
    requesting,
    leaving,
    error,
    canJoin,
    canLeave,
    joinGroup,
    leaveGroup,
    getStatusMessage,
  };
};
